import { Order, VoucherRewardType } from '@diamond/shared/types';

type MockVoucherDiscount = {
  product_item_code: number;
  product_name: string;
  division: 'trading' | 'diamond' | 'equipment' | 'fnb' | 'food';
  quantity: number;
  original_subtotal: number;
  discount_amount: number | null;
  discount_percent: number | null;
  saved_amount: number;
  final_subtotal: number;
  reward_type: VoucherRewardType;
};

type MockVoucherFreeGood = {
  product_item_code: number;
  product_name: string;
  division: 'trading' | 'diamond' | 'equipment' | 'fnb' | 'food';
  quantity: number;
  saved_amount: number;
  reward_type: VoucherRewardType;
  product_images: {
    image: string;
    thumbnail_url: string | null;
    medium_image_url: string | null;
    large_image_url: string | null;
  }[];
};

type MockVoucherConfig = {
  rewardType: VoucherRewardType;
  code?: string;
  title?: string;
  discountPercent?: number;
  discountAmount?: number;
  freeGoodsQuantity?: number;
  productItemCode?: number;
};

type MockVoucherOptions = {
  discountPercent?: number;
  discountAmount?: number;
  freeGoodsQuantity?: number;
  productItemCode?: number;
  rewardTypes?: VoucherRewardType[];
  voucherConfigs?: MockVoucherConfig[];
};

export type ProductRewardImages = {
  image: string;
  thumbnail_url: string | null;
  medium_image_url: string | null;
  large_image_url: string | null;
};

export type ProductRewardBase = {
  product_item_code: string;
  product_name: string;
  quantity: number;
  images: ProductRewardImages;
};

export type PromoReward = ProductRewardBase & {
  source: 'promo';
  promo_id: string;
  mechanism: string;
};

export type VoucherReward = ProductRewardBase & {
  source: 'voucher';
  voucher_id: string;
  voucher_code: string;
  voucher_name: string;
};

export type ProductRewardsResponse = {
  promo_rewards: PromoReward[];
  voucher_rewards: VoucherReward[];
};

export type ProductRewardsOverrides = Partial<ProductRewardsResponse>;

export function buildMockProductRewards(
  overrides: ProductRewardsOverrides = {}
): ProductRewardsResponse {
  const defaultPromoRewards: PromoReward[] = [
    {
      source: 'promo',
      promo_id: 'promo-uuid-1',
      mechanism:
        'Anda mendapatkan produk gratis sesuai ketentuan promo yang berlaku.',
      product_item_code: '999888',
      product_name: 'Produk Free Goods Promo',
      quantity: 5000,
      images: {
        image: 'https://cdn.example.com/products/999888.jpg',
        thumbnail_url: null,
        medium_image_url: null,
        large_image_url: null,
      },
    },
    {
      source: 'promo',
      promo_id: 'promo-uuid-2',
      mechanism:
        'Anda mendapatkan produk gratis sesuai ketentuan promo yang berlaku.',
      product_item_code: '777666',
      product_name: 'Gift Item',
      quantity: 2000,
      images: {
        image: 'https://cdn.example.com/products/777666.jpg',
        thumbnail_url: null,
        medium_image_url: null,
        large_image_url: null,
      },
    },
  ];

  const defaultVoucherRewards: VoucherReward[] = [
    {
      source: 'voucher',
      voucher_id: 'voucher-uuid-1',
      voucher_code: 'FREEGOODS123',
      voucher_name: 'Voucher Gratis Produk',
      product_item_code: '123456',
      product_name: 'Produk FG Voucher',
      quantity: 3000,
      images: {
        image: 'https://cdn.example.com/products/123456.jpg',
        thumbnail_url: null,
        medium_image_url: null,
        large_image_url: null,
      },
    },
    {
      source: 'voucher',
      voucher_id: 'voucher-uuid-2',
      voucher_code: 'BONUS99',
      voucher_name: 'Voucher Bonus Item',
      product_item_code: '654321',
      product_name: 'Bonus Product',
      quantity: 1000,
      images: {
        image: 'https://cdn.example.com/products/654321.jpg',
        thumbnail_url: null,
        medium_image_url: null,
        large_image_url: null,
      },
    },
  ];

  return {
    promo_rewards: overrides.promo_rewards ?? defaultPromoRewards,
    voucher_rewards: overrides.voucher_rewards ?? defaultVoucherRewards,
  };
}

type VoucherSummary = NonNullable<Order['vouchers']>;
type AppliedVoucher = VoucherSummary['applied_vouchers'][number];
type RewardByItemCode = VoucherSummary['reward_by_item_code'];

/**
 * Inject mock voucher data into an order to showcase voucher UI states.
 *
 * @param order - The order data to enrich.
 * @param options - Configure mock reward amounts, targeted product codes, voucher reward types, or per-voucher configs.
 */
export function injectMockVoucherData(
  order: Order,
  options: MockVoucherOptions = {}
): Order {
  const {
    discountPercent = 10,
    discountAmount = null,
    freeGoodsQuantity = 0,
    productItemCode,
    rewardTypes = ['DISCOUNT_PERCENT', 'DISCOUNT_AMOUNT', 'FREE_GOODS'],
    voucherConfigs = [],
  } = options;

  const uniqueRewardTypes = [...new Set(rewardTypes)] as VoucherRewardType[];

  const voucherSources: MockVoucherConfig[] =
    voucherConfigs.length > 0
      ? voucherConfigs
      : uniqueRewardTypes.map((rewardType) => ({ rewardType }));

  const modifiedOrder = { ...order };
  const voucherResults = voucherSources.map((source) => ({
    source,
    discounts: [] as MockVoucherDiscount[],
    freeGoods: [] as MockVoucherFreeGood[],
  }));

  modifiedOrder.items = modifiedOrder.items.map((item) => {
    const modifiedItem = { ...item };
    modifiedItem.products = modifiedItem.products.map((product) => {
      const modifiedProduct = { ...product };

      const mockVoucherDiscounts: MockVoucherDiscount[] = [];
      const mockVoucherFreeGoods: MockVoucherFreeGood[] = [];

      const productDivision =
        (product.division as
          | 'trading'
          | 'diamond'
          | 'equipment'
          | 'fnb'
          | 'food') ?? 'diamond';

      const originalPrice = product.price ?? 0;
      const quantity = product.quantity ?? 0;
      const originalSubtotal = originalPrice * (quantity / 1000);

      voucherSources.forEach((voucherConfig) => {
        const voucherResult = voucherResults.find(
          (result) => result.source === voucherConfig
        );
        const targetProductCode =
          voucherConfig.productItemCode ?? productItemCode;
        const shouldAddVoucher =
          !targetProductCode || product.item_code === targetProductCode;

        if (!shouldAddVoucher) {
          return;
        }

        const effectivePercent =
          typeof voucherConfig.discountPercent === 'number'
            ? voucherConfig.discountPercent
            : discountPercent;
        const effectiveAmount =
          typeof voucherConfig.discountAmount === 'number'
            ? voucherConfig.discountAmount
            : discountAmount;
        const effectiveFreeGoods =
          typeof voucherConfig.freeGoodsQuantity === 'number'
            ? voucherConfig.freeGoodsQuantity
            : freeGoodsQuantity;

        const percentSavedAmount =
          effectivePercent > 0
            ? (originalSubtotal * effectivePercent) / 100
            : null;
        const amountSavedFromOption =
          effectiveAmount !== null ? effectiveAmount : null;

        if (
          voucherConfig.rewardType !== 'FREE_GOODS' &&
          (effectivePercent > 0 || amountSavedFromOption !== null)
        ) {
          if (
            voucherConfig.rewardType === 'DISCOUNT_PERCENT' &&
            percentSavedAmount !== null
          ) {
            const percentDiscount: MockVoucherDiscount = {
              product_item_code: product.item_code,
              product_name: product.title ?? product.desc ?? '',
              division: productDivision,
              quantity: quantity,
              original_subtotal: originalSubtotal,
              discount_amount: null,
              discount_percent: effectivePercent,
              saved_amount: percentSavedAmount,
              final_subtotal: originalSubtotal - percentSavedAmount,
              reward_type: 'DISCOUNT_PERCENT',
            };

            mockVoucherDiscounts.push(percentDiscount);
            voucherResult?.discounts.push(percentDiscount);
          }

          if (voucherConfig.rewardType === 'DISCOUNT_AMOUNT') {
            const amountSaved =
              amountSavedFromOption !== null
                ? amountSavedFromOption
                : percentSavedAmount;

            if (amountSaved !== null) {
              const amountDiscountPercent =
                originalSubtotal > 0
                  ? (amountSaved / originalSubtotal) * 100
                  : null;

              const amountDiscount: MockVoucherDiscount = {
                product_item_code: product.item_code,
                product_name: product.title ?? product.desc ?? '',
                division: productDivision,
                quantity: quantity,
                original_subtotal: originalSubtotal,
                discount_amount: amountSaved,
                discount_percent: amountDiscountPercent,
                saved_amount: amountSaved,
                final_subtotal: originalSubtotal - amountSaved,
                reward_type: 'DISCOUNT_AMOUNT',
              };

              mockVoucherDiscounts.push(amountDiscount);
              voucherResult?.discounts.push(amountDiscount);
            }
          }
        }

        if (
          voucherConfig.rewardType === 'FREE_GOODS' &&
          effectiveFreeGoods > 0
        ) {
          const productImages = product.product_images ?? [];
          const productImageThumbnails = product.product_image_thumbnails ?? [];
          const productImagesMedium = product.product_images_medium ?? [];
          const productImagesLarge = product.product_images_large ?? [];

          const maxLength = Math.max(
            productImages.length,
            productImageThumbnails.length,
            productImagesMedium.length,
            productImagesLarge.length
          );

          const transformedProductImages = Array.from(
            { length: maxLength },
            (_, index) => ({
              image: productImages[index] ?? '',
              thumbnail_url: productImageThumbnails[index] ?? null,
              medium_image_url: productImagesMedium[index] ?? null,
              large_image_url: productImagesLarge[index] ?? null,
            })
          );

          const freeGood: MockVoucherFreeGood = {
            product_item_code: product.item_code,
            product_name: product.title ?? product.desc ?? '',
            division: productDivision,
            quantity: effectiveFreeGoods * 1000,
            saved_amount: 0,
            product_images: transformedProductImages,
            reward_type: 'FREE_GOODS',
          };

          mockVoucherFreeGoods.push(freeGood);
          voucherResult?.freeGoods.push(freeGood);
        }
      });

      return {
        ...modifiedProduct,
        voucher_discounts: mockVoucherDiscounts,
        voucher_free_goods: mockVoucherFreeGoods,
      };
    });

    return modifiedItem;
  });

  const existingVoucherSummary: VoucherSummary = {
    applied_vouchers: modifiedOrder.vouchers?.applied_vouchers ?? [],
    total_voucher_discount: modifiedOrder.vouchers?.total_voucher_discount ?? 0,
    reward_by_item_code:
      (modifiedOrder.vouchers?.reward_by_item_code as RewardByItemCode) ?? {},
  };

  const rewardByItemCode: RewardByItemCode = {
    ...existingVoucherSummary.reward_by_item_code,
  };

  const ensureRewardBucket = (itemCode: number) => {
    const key = String(itemCode);
    if (!rewardByItemCode[key]) {
      rewardByItemCode[key] = { discounts: [], free_goods: [] };
    }
    return rewardByItemCode[key];
  };

  voucherResults.forEach((result) => {
    result.discounts.forEach((discount) => {
      ensureRewardBucket(discount.product_item_code).discounts.push(discount);
    });
    result.freeGoods.forEach((freeGood) => {
      ensureRewardBucket(freeGood.product_item_code).free_goods.push(freeGood);
    });
  });

  const appliedVouchers: AppliedVoucher[] = voucherSources.map(
    (voucherConfig, index) => {
      const orderId = String(modifiedOrder.id ?? 'mock-order-id');
      const rewardType = voucherConfig.rewardType;
      const result = voucherResults[index];
      const discounts = rewardType === 'FREE_GOODS' ? [] : result.discounts;
      const freeGoods = rewardType === 'FREE_GOODS' ? result.freeGoods : [];
      const totalDiscountAmount = discounts.reduce(
        (sum, discount) => sum + discount.saved_amount,
        0
      );

      const now = new Date();
      const periodStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const periodEnd = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      return {
        order_voucher_id: `mock-order-voucher-${index + 1}`,
        voucher_id: `mock-voucher-id-${index + 1}`,
        order_id: orderId,
        voucher_code: voucherConfig.code ?? `MOCK-${rewardType}-${index + 1}`,
        voucher_title:
          voucherConfig.title ??
          `Mock ${String(rewardType).replace(/_/g, ' ').toLowerCase()}`,
        voucher_target_type: 'GENERAL',
        period_start: periodStart.toISOString(),
        period_end: periodEnd.toISOString(),
        reward_type: rewardType,
        total_discount_amount: totalDiscountAmount,
        min_purchase_amount: null,
        min_total_quantity_all_item: null,
        is_bundling: false,
        allow_other_vouchers: true,
        allow_other_promos: true,
        allow_special_prices: true,
        product_discounts: discounts,
        free_goods: freeGoods,
      };
    }
  );

  const totalVoucherDiscount = voucherResults.reduce((sum, result) => {
    const discountTotal = result.discounts.reduce(
      (discountSum, discount) => discountSum + discount.saved_amount,
      0
    );
    return sum + discountTotal;
  }, 0);

  modifiedOrder.vouchers = {
    applied_vouchers: [
      ...existingVoucherSummary.applied_vouchers,
      ...appliedVouchers,
    ],
    total_voucher_discount:
      existingVoucherSummary.total_voucher_discount + totalVoucherDiscount,
    reward_by_item_code: rewardByItemCode,
  };
  modifiedOrder.total_voucher_discount =
    existingVoucherSummary.total_voucher_discount + totalVoucherDiscount;

  return modifiedOrder;
}
